import { CaseSearchPanel, CaseSearchResults } from '@case-search/components';
import type { UserType } from '@case-search/domain';
import { SearchType } from '@case-search/domain';
import { useCaseSearch } from '@case-search/hooks';
import { LoadingMessage } from '@communities/community/components';
import { useCommunityDetail } from '@communities/community/hooks';
import { ErrorBanner } from '@core/components';
import { HeroBackground } from '@core/components/HeroBackground';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Heading } from '@soteria/design-system-ve';
import { useState, useCallback } from 'react';

import * as styles from './CaseSearchPage.css';

/**
 * Case Search Page Component
 *
 * Provides search interface for finding moderation cases by User ID and User Type
 * Player scores are computed dynamically by aggregating enforcement/case documents
 */
function CaseSearchPageComponent() {
  const { community, loading } = useCommunityDetail();
  const { searchResult, isSearching, errors, search } = useCaseSearch();
  const [hasSearched, setHasSearched] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined);
  const [featureInProgressMessage, setFeatureInProgressMessage] = useState<string | undefined>(undefined);

  const handleSearch = useCallback(
    async (searchType: SearchType, searchValue: string, userType?: UserType) => {
      // Clear previous messages
      setValidationMessage(undefined);
      setFeatureInProgressMessage(undefined);

      // Check if feature is not yet implemented
      if (searchType === SearchType.SUBJECT_ID || searchType === SearchType.CASE_ID) {
        setFeatureInProgressMessage('Working on this feature');
        setHasSearched(true);

        return;
      }

      // Validate User ID search
      if (searchType === SearchType.USER_ID) {
        if (!userType) {
          setValidationMessage('Please select a User Type');
          setHasSearched(true);

          return;
        }

        // Perform search
        if (!community?.id) return;

        await search({
          searchType,
          searchValue,
          userType,
          communityId: community.id,
        });

        setHasSearched(true);
      }
    },
    [community, search]
  );

  return (
    <>
      <HeroBackground imageUrl={community?.superHeroArtUrl} />
      {loading ? (
        <LoadingMessage />
      ) : (
        <div>
          {/* Header Section */}
          <div className={styles.headerSection}>
            {/* Hero Overlay for smooth gradient transition */}
            <div className={styles.heroOverlay} />

            {/* Page Header */}
            <Heading className={styles.pageHeader} level="h1">
              Search {community?.name || 'Community'} Cases
            </Heading>

            {/* Search Panel */}
            <div className={styles.searchFiltersContainer}>
              <CaseSearchPanel isLoading={isSearching} onSearch={handleSearch} />
            </div>
          </div>

          {/* Content Section with Solid Background */}
          <div className={styles.contentSection}>
            <div className={styles.contentContainer}>
              {/* Error Banner */}
              {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error searching for cases:" />}

              {/* Search Results or Empty State */}
              <CaseSearchResults
                featureInProgressMessage={featureInProgressMessage}
                hasSearched={hasSearched}
                isLoading={isSearching}
                results={searchResult}
                validationMessage={validationMessage}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const CaseSearchPage = withAppInsightsTracking(CaseSearchPageComponent, 'CaseSearchPage');
